interface BookingResponse {
  ServiceStatus?: {
    Success?: boolean
  }
  ServiceResult?: {
    Status?: string
  }
}

interface BookingSession {
  SessionId?: string
}

interface BookingSessionValidationOptions {
  lessonsRemaining: number
  lessonsPerSession?: number
  expectedLessonCount?: number
}

export const getBookableLessonsCount = (lessonsRemaining: number = 0, totalLessons?: number) => {
  const normalizedRemaining = Math.max(0, lessonsRemaining)

  return typeof totalLessons === 'number' && totalLessons > 0
    ? Math.min(normalizedRemaining, totalLessons)
    : normalizedRemaining
}

export const isBookingSuccessful = (response?: BookingResponse | null) =>
  response?.ServiceStatus?.Success === true && response?.ServiceResult?.Status?.toLowerCase() === 'success'

export const validateBookingSessions = <T extends BookingSession>(
  sessions: T[],
  { lessonsRemaining, lessonsPerSession = 1, expectedLessonCount }: BookingSessionValidationOptions
) => {
  const sessionIds = sessions.map(({ SessionId }) => SessionId).filter(Boolean) as string[]
  const lessonCount = sessions.length * lessonsPerSession

  if (!sessions.length) {
    return { isValid: false, lessonCount, reason: 'No sessions selected' }
  }

  if (sessionIds.length !== sessions.length) {
    return { isValid: false, lessonCount, reason: 'One or more sessions are missing an id' }
  }

  if (new Set(sessionIds).size !== sessions.length) {
    return { isValid: false, lessonCount, reason: 'Duplicate sessions selected' }
  }

  if (lessonCount > lessonsRemaining) {
    return { isValid: false, lessonCount, reason: 'Selected lessons exceed the lessons remaining' }
  }

  if (expectedLessonCount !== undefined && lessonCount !== expectedLessonCount) {
    return { isValid: false, lessonCount, reason: 'Selected lessons do not match the booking summary' }
  }

  return { isValid: true, lessonCount }
}
